import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Crosshair as Radar, ShieldWarning, Warning, GitBranch, Bug, Pulse,
  TrendUp, TrendDown,
} from "@phosphor-icons/react";

const HEALTH_TONE = {
  green: "bg-emerald-100 text-emerald-800 border-emerald-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  red:   "bg-red-100 text-red-800 border-red-300",
};
const DOT = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };

export default function PortfolioRiskRadar() {
  const [kpi, setKpi] = useState(null);
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/kpi/portfolio"),
          api.get("/projects/_meta/risk-portfolio"),
        ]);
        setKpi(a.data); setRisk(b.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading portfolio radar…</div>;
  if (!kpi || !risk) return null;

  const totals = kpi.totals || {};
  const bands = kpi.band_counts || { green: 0, amber: 0, red: 0 };

  return (
    <section className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 p-5 shadow-sm" data-testid="portfolio-risk-radar">
      <header className="mb-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Radar size={20} className="text-[#1E5CBA]" weight="duotone" />
          <h2 className="font-display text-lg font-semibold text-slate-900">Portfolio Risk Radar</h2>
          <span className="hidden md:inline text-xs text-slate-500">Real-time roll-up across all active projects</span>
        </div>
        <Link to="/projects" className="text-xs font-medium text-[#1E5CBA] hover:underline">Browse projects →</Link>
      </header>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Hero label="Active projects" value={totals.count || 0} tone="slate" icon={Pulse} />
        <Hero label="Avg health"
              value={totals.avg_health_weighted != null ? `${totals.avg_health_weighted}` : "—"}
              tone={(totals.avg_health_weighted || 0) >= 80 ? "green" : (totals.avg_health_weighted || 0) >= 60 ? "amber" : "red"}
              icon={(totals.avg_health_weighted || 0) >= 70 ? TrendUp : TrendDown} />
        <Hero label="Weighted SPI"
              value={totals.avg_spi_weighted != null ? totals.avg_spi_weighted.toFixed(2) : "—"}
              tone={(totals.avg_spi_weighted || 0) >= 0.95 ? "green" : (totals.avg_spi_weighted || 0) >= 0.85 ? "amber" : "red"}
              icon={ShieldWarning} />
        <Hero label="Weighted CPI"
              value={totals.avg_cpi_weighted != null ? totals.avg_cpi_weighted.toFixed(2) : "—"}
              tone={(totals.avg_cpi_weighted || 0) >= 0.95 ? "green" : (totals.avg_cpi_weighted || 0) >= 0.85 ? "amber" : "red"}
              icon={ShieldWarning} />
        <Hero label="Health bands"
              value={`${bands.green}G·${bands.amber}A·${bands.red}R`}
              tone="slate"
              icon={Radar} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Project dots — sorted by health (worst first) */}
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-sm font-semibold text-slate-900">Projects at a glance</h3>
            <span className="text-[10px] font-mono-alt text-slate-500 uppercase">Sorted: worst → best</span>
          </div>
          {kpi.projects && kpi.projects.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {kpi.projects.map((p) => (
                <Link key={p.id} to={`/projects?focus=${p.id}`}
                  className="group inline-flex items-center gap-1.5 rounded-lg border bg-slate-50 px-2.5 py-1.5 transition hover:bg-white hover:shadow-md"
                  title={`${p.name} · health ${p.health_score} · SPI ${p.spi ?? "—"} · CPI ${p.cpi ?? "—"} · phase ${p.current_phase}`}
                  data-testid={`radar-dot-${p.id}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${DOT[p.health_band]}`} />
                  <span className="text-xs font-medium text-slate-700 truncate max-w-[150px]">{p.name}</span>
                  <span className="text-[10px] font-mono-alt text-slate-500">{p.health_score}</span>
                </Link>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 italic">No active projects yet.</p>}
        </div>

        {/* Risk posture */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Open work posture</h3>
          <ul className="space-y-2 text-sm">
            <PostureRow icon={ShieldWarning} label="Risks open" value={risk.risk?.total_open} sub={`${risk.risk?.band_counts?.critical || 0} critical`} link="/projects" tone={(risk.risk?.band_counts?.critical || 0) > 0 ? "red" : "slate"} />
            <PostureRow icon={Warning} label="Issues open" value={risk.issue?.total_open} sub={`${risk.issue?.breached || 0} SLA-breached`} link="/projects" tone={(risk.issue?.breached || 0) > 0 ? "red" : "slate"} />
            <PostureRow icon={GitBranch} label="CR backlog" value={risk.change_requests?.pending} sub={`${risk.change_requests?.tier_4_pending || 0} tier-4`} link="/projects" tone="slate" />
            <PostureRow icon={Bug} label="NCRs open" value={(kpi.projects || []).reduce((a, p) => a + (p.open_work?.ncrs_open || 0), 0)} sub={`${(kpi.projects || []).reduce((a, p) => a + (p.open_work?.ncrs_critical || 0), 0)} critical`} link="/projects" tone="slate" />
          </ul>
        </div>
      </div>

      {/* Top critical risks */}
      {risk.risk?.top_5?.length > 0 && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50/40 p-4">
          <h3 className="font-display text-sm font-semibold text-red-900 mb-2">Top-5 risks by severity</h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {risk.risk.top_5.map((r) => (
              <li key={r.id} className="rounded-lg border border-red-200 bg-white p-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`inline-block rounded px-1.5 py-0.5 font-mono-alt text-[9px] uppercase ${r.severity_band === "critical" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{r.severity} · {r.severity_band}</span>
                  <span className="font-medium text-slate-900 line-clamp-1 flex-1">{r.title}</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">{r.project_name} · L{r.likelihood}×I{r.impact}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function Hero({ label, value, tone = "slate", icon: Icon }) {
  const palette = {
    slate: "bg-slate-50 border-slate-200 text-slate-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    red:   "bg-red-50 border-red-200 text-red-900",
  }[tone];
  return (
    <div className={`rounded-xl border p-3 ${palette}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono-alt uppercase tracking-widest opacity-70">{label}</div>
        {Icon && <Icon size={14} className="opacity-50" />}
      </div>
      <div className="mt-1 font-display text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function PostureRow({ icon: Icon, label, value, sub, link, tone }) {
  const c = tone === "red" ? "text-red-700" : "text-slate-700";
  return (
    <li>
      <Link to={link || "#"} className="flex items-center gap-2 rounded-lg border border-transparent px-1 py-1 hover:border-slate-200 hover:bg-slate-50">
        <Icon size={14} className={c} />
        <span className="text-xs flex-1">{label}</span>
        <span className={`font-display text-lg font-bold tabular-nums ${c}`}>{value ?? 0}</span>
        {sub && <span className="text-[10px] text-slate-500">{sub}</span>}
      </Link>
    </li>
  );
}
